import React, { useMemo } from 'react';
import { useTasks, Task } from '../../crm-tasks/useData';

export interface TasksCountByStatusProps {
  isLoading?: boolean;
  embedded?: boolean;
}

interface StatusCount {
  status: string;
  count: number;
  config: {
    bgColor: string;
    textColor: string;
    borderColor: string;
    icon: JSX.Element;
  };
}

const cardClass = (embedded?: boolean) =>
  embedded ? 'min-w-0' : 'bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100';

const TasksCountByStatus: React.FC<TasksCountByStatusProps> = ({ isLoading: externalLoading, embedded }) => {
  const { tasks, isLoading: tasksLoading } = useTasks();
  const isLoading = externalLoading || tasksLoading;

  const statusCounts = useMemo(() => {
    const tasksArray = Array.isArray(tasks) ? tasks : [];
    
    // Define the specific statuses we want to display
    const targetStatuses = ['Not Started', 'In Progress', 'Awaiting Feedback'];
    
    // Map to store counts for each target status (case-insensitive matching)
    const statusMap = new Map<string, number>();
    
    // Initialize all target statuses with 0 count
    targetStatuses.forEach(status => {
      statusMap.set(status, 0);
    });

    // Count tasks for each target status (case-insensitive matching)
    tasksArray.forEach((task: Task) => {
      const taskStatus = task.taskStatusDescr?.ts_descr || '';
      if (!taskStatus) return;
      
      // Find matching target status (case-insensitive)
      const matchingStatus = targetStatuses.find(targetStatus => 
        taskStatus.toLowerCase() === targetStatus.toLowerCase() ||
        taskStatus.toLowerCase().includes(targetStatus.toLowerCase()) ||
        targetStatus.toLowerCase().includes(taskStatus.toLowerCase())
      );
      
      if (matchingStatus) {
        statusMap.set(matchingStatus, (statusMap.get(matchingStatus) || 0) + 1);
      }
    });

    // Define colors and icons for each status
    const getStatusConfig = (status: string): { bgColor: string; textColor: string; borderColor: string; icon: JSX.Element } => {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('not started')) {
        return {
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-700',
          borderColor: 'border-yellow-200',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      }
      if (statusLower.includes('in progress') || statusLower.includes('progress')) {
        return {
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
        };
      }
      if (statusLower.includes('awaiting feedback') || statusLower.includes('awaiting')) {
        return {
          bgColor: 'bg-teal-50',
          textColor: 'text-teal-700',
          borderColor: 'border-teal-200',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          ),
        };
      }
      return {
        bgColor: 'bg-gray-50',
        textColor: 'text-gray-700',
        borderColor: 'border-gray-200',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        ),
      };
    };

    // Create status count array with all target statuses (even if count is 0)
    const counts: StatusCount[] = targetStatuses.map(status => ({
      status,
      count: statusMap.get(status) || 0,
      config: getStatusConfig(status),
    }));

    return counts;
  }, [tasks]);

  const totalTasks = useMemo(() => {
    return statusCounts.reduce((sum, item) => sum + item.count, 0);
  }, [statusCounts]);

  if (isLoading) {
    return (
      <div className={cardClass(embedded)}>
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Tasks by Status</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-gray-100 rounded-lg p-4 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-2/3 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (statusCounts.length === 0) {
    return (
      <div className={cardClass(embedded)}>
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Tasks by Status</h2>
        <div className="text-center py-6 sm:py-8 text-gray-500">
          <svg className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm sm:text-base">No tasks found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass(embedded)}>
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Tasks by Status</h2>
        {totalTasks > 0 && (
          <span className="text-xs sm:text-sm text-gray-500 font-medium">
            {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'} total
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {statusCounts.map((statusItem, index) => {
          const percentage = totalTasks > 0 ? (statusItem.count / totalTasks) * 100 : 0;
          
          return (
            <div
              key={index}
              className={`${statusItem.config.bgColor} ${statusItem.config.borderColor} border-2 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:shadow-md transition-all duration-200`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`${statusItem.config.textColor} flex items-center gap-2`}>
                  {statusItem.config.icon}
                  <span className="text-xs sm:text-sm font-medium truncate">{statusItem.status}</span>
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`${statusItem.config.textColor} text-2xl sm:text-3xl font-bold`}>
                  {statusItem.count}
                </span>
                <span className={`${statusItem.config.textColor} text-xs sm:text-sm font-medium opacity-75`}>
                  {statusItem.count === 1 ? 'task' : 'tasks'}
                </span>
              </div>
              {totalTasks > 0 && (
                <div className="mt-3 pt-3 border-t border-opacity-20 border-current">
                  <div className="flex items-center justify-between">
                    <span className={`${statusItem.config.textColor} text-[10px] sm:text-xs font-medium opacity-75`}>
                      {percentage.toFixed(1)}% of total
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TasksCountByStatus;

